import {BasePageProps, EmptyGuid, Roles} from 'app/types'
import {
  Booth,
  EndReason,
  InterviewSession,
  QueueUser,
  QueueUserStatus,
  GroupType,
  UserStatus,
  VJFPUser,
  VideoSessionStatus,
  CreateBoothInput,
} from 'app/API'
import {BoxArrowInUpRight, ChatQuoteFill, PencilSquare, Recycle} from 'react-bootstrap-icons'
import {Button, Col, Container, Form, OverlayTrigger, Row, Tooltip} from 'react-bootstrap'
import {ChangeQueueUserPubsub, LocalAppEventPubSub} from 'app/app-events'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {getInitials, getUserStatus, UrlParamReplace} from 'app/helpers/MiscUtils'
import {
  setupInterviewSessionSubscriptionsByRecruiterId,
  setupQueueSubscriptionsByBoothId,
} from 'app/amplify-subscriptions'

import {AppModal} from 'app/modules/shared/modals/AppModal'
import {AppTopNav} from 'app/modules/shared/AppTopNav'
import {BoothService, ActiveBoothViewCounterState} from 'app/services/BoothService'
import {JobSeekerListItem} from 'app/modules/shared/profile/JobSeekerListItem'
import {MessageService} from 'app/services/MessageService'
import {QueueUserService} from 'app/services/QueueUserService'
import {RecruiterListItem} from 'app/modules/shared/profile/RecruiterListItem'
import {RecruiterRoutes} from '../RecruiterPage'
import {VJFPUserService} from 'app/services/VJFPUserService'
import {useAuth} from 'app/context/auth-context'
import {useHistory} from 'react-router-dom'
import {useAppContext} from 'app/context/app-context'
import BlockUi from 'react-block-ui'
import 'react-block-ui/style.css'
import {AlertModal} from 'app/modules/shared/modals/AlertModal'
import {LocalAppEventType} from 'app/app-models'
import _ from 'lodash'
import {useDebounceFn, usePresence} from 'app/hooks'
import {QuestionAnswerService} from 'app/services/QuestionAnswerService'
import useIsMounted from 'app/hooks/useIsMounted'
import {RxSubscription} from 'app/RxSubscriptions'
import {AvailabilitySelector} from 'app/modules/shared/availability-selector/AvailabilitySelector'
import {InterviewSessionService} from 'app/services/InterviewSessionService'
import {Alert, Modal} from 'react-bootstrap-v5'
import CreateEditBooth from 'app/modules/employer-admin/components/booths/CreateEditBooth'
import {useS3Object} from 'app/context/S3ObjectContext'
import {EmployerAdminRoutes} from 'app/modules/employer-admin/EmployerAdminPage'
import {OrgService, Organization} from 'app/services/OrgService'

const questionAnswerService = new QuestionAnswerService()
export const RecruiterBoothView = (props: BasePageProps) => {
  console.log('re-rendering recruiter booth view')
  const {user} = useAuth()
  const {jobFair} = useAppContext()
  const [loading, setLoading] = useState<boolean>(false)
  const [booth, setBooth] = useState<Booth | undefined | null>(undefined)

  const [showBroadCastModal, setBroadCastModal] = useState<boolean>(false)
  const [broadCastType, setBroadCastType] = useState<'Booth' | 'Queue'>('Booth')
  const [broadCastMessage, setBroadCastMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const [totalQuestions, setTotalQuestions] = useState<number | null>(null)
  const messageService = new MessageService()
  const queueUserService = new QueueUserService()
  const history = useHistory()
  const [queueUsers, setQueueUsers] = useState<QueueUser[]>([])
  const [VJFPUsers, setVJFPUsers] = useState<VJFPUser[]>([])
  const [recruiters, setRecruiters] = useState<VJFPUser[]>([])
  const [disableStartInterview, setDisableStartInterview] = useState<boolean>(false)

  const vjfpUsersWithPresence = usePresence(VJFPUsers)
  const recruitersWithPresence = usePresence(recruiters)

  const recruitersSorted = useMemo(() => {
    return [...recruitersWithPresence].sort((a, b) => {
      const p1 = a.presenceStatus === UserStatus.LOGGED_IN ? 1 : 0
      const p2 = b.presenceStatus === UserStatus.LOGGED_IN ? 1 : 0
      return p2 - p1
    })
  }, [recruitersWithPresence])
  const {getS3ObjectUrl, s3ObjectCache} = useS3Object()

  const [refreshDisable, setRefreshDisable] = useState<boolean>(false)
  const isMounted = useIsMounted('RecruiterBoothView')
  const refreshing = useRef<boolean>(false)
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [showEnded, setShowEnded] = useState<boolean>(false)
  const [showManageBooth, setShowManageBooth] = useState<boolean>(false)
  const [org, setOrg] = useState<Organization>()
  const companyImageUrl = booth?.companyImageUrl
    ? s3ObjectCache[booth.companyImageUrl] || ''
    : org?.companyLogo
    ? s3ObjectCache[org.companyLogo] || ''
    : ''

  const quInterviewTimeoutRef = useRef<NodeJS.Timeout | undefined>()
  const disposablesRef = useRef<any[]>([])

  /* Setup  (page load)
   * - get booth for current user (recruiter)
   * - list job seekers in booth.
   * - list all recruiters in booth.
   * - Calculate 'Job Seeker Booth State' for each job seeker in booth and set as initial booth state. (See Below)
   *
   * - Subscribe to all queue users for current jobfair (additions and deletions)
   *   . For queue user's in the current booth
   *       -- When Job Seeker added:
   *          -- Subscribe to the job seeker's presence updates
   *          -- Calculate Job Seeker's Booth Status
   *
   *       -- When Job Seeker removed: Dispose the Job Seeker's presence updates
   *
   *   . For queue user updates in other booths
   *      -- Determine if user is present in current booth
   *          present: Calculate User's Booth Status
   *          not present: Skip.
   *
   * - Subscribe Job Seeekers presence updates (status changes)
   *      -- When presence changed: Caluclate Job Seeker's Booth Status by refreshing Queue
   *
   * - For each user calculate 'user's booth state'. This is the state that should be applied to the user based on the user's state in all queues.  (see Calculate User's Booth State)
   *
   *
   *  Calculate User's booth state:
   *    Assumption: Booth Job Seekers are sorted by priority (-1 interviewing, 0 next to interview, 1 invited, >= 2 Score)
   *    Calculate the number of "free next-to-interview slots" this is the number of: current-users-in-next-to-interview-state + current-users-intreviewing 'minus' the-number-of-logged-in-recruiters
   *    Fetch all the user's queue participations
   *      -- if any participatin is in status "Interviewing" or "next to interview" (queue user priority IN [-1, 0]) set the user's status as busy and continue with next.
   *      -- if there are available next-to-interview slots, set the user's booth status in next to interview: update priority to 0
   */

  const fetchBoothByRecruiter = useCallback(async () => {
    setLoading(true)

    if (!jobFair?.id || !user?.id) return

    const boothService = new BoothService()

    try {
      const _booth = await boothService.getBoothByRecruiter(jobFair.id, user.id)

      if (!isMounted()) return
      setLoading(false)
      if (_booth == null) {
        alert('You are not assigned to any booth')
        setBooth(null)
      }
      if (_booth?.id) {
        if (_booth.companyImageUrl) {
          getS3ObjectUrl(_booth.companyImageUrl)
        }
        if (_booth.recruiters && _booth.recruiters.includes(user.id!)) {
          setBooth(_booth)
          window.sessionStorage.setItem('recruiterBoothId', _booth.id)
        } else {
          alert('You are not assigned to any booth')
          setBooth(null)
          window.sessionStorage.removeItem('recruiterBoothId')
        }
      }
    } catch (error) {
      if (isMounted()) setLoading(false)
    }
  }, [jobFair?.id, user?.id])

  // Get Booth for user (recruiter)
  useEffect(() => {
    fetchBoothByRecruiter()
    return () => {
      window.sessionStorage.removeItem('recruiterBoothId')
    }
  }, [jobFair?.id, user?.id])

  useEffect(() => {
    // Check if user is loaded and has ID
    if (user?.id) {
      // Check if jobFair is null or booth is null AND user has EmployerAdmin role. Note JobFair is null or Booth is null only when API hit is resolved before API hit they would be undefined
      if ((jobFair === null || booth === null) && user.roles?.includes(Roles.EmployerAdmin)) {
        // Redirect to EmployerAdmin Manage Booths (step 4)
        history.replace(EmployerAdminRoutes.Dashboard + '?step=4')
      }
    }
  }, [user, jobFair, booth, history])

  useEffect(() => {
    if (user?.organizationId) {
      new OrgService().getOrgById<Organization>(user.organizationId).then((orgDetail) => {
        if (isMounted()) setOrg(orgDetail)
      })
    }
  }, [user?.organizationId])

  useEffect(() => {
    if (org?.companyLogo) {
      getS3ObjectUrl(org.companyLogo)
    }
  }, [org?.companyLogo, getS3ObjectUrl])

  // List all Job Seekers for the current's Booth Queue
  // List all recruiters for the current booth

  useEffect(() => {
    if (!jobFair?.id || !booth?.id) return

    setLoading(true)

    refresh()
      .then(async () => {
        if (!isMounted()) return
        setLoading(false)
        //setInterval(async () => {
        if (!refreshing.current) {
          await refresh()
        }
        //}, 10000);
      })
      .catch((err) => {
        console.error(err)
        setLoading(false)
      })

    const _groups = booth.id.split('-')

    questionAnswerService
      .getAllQueueQuestionsByBoothId(jobFair.id, _groups.length == 2 ? _groups[1] : booth.id)
      .then((queueQuestions) => setTotalQuestions(queueQuestions?.length || 0))
  }, [jobFair?.id, booth?.id])

  // Subscribe to Queue Users
  // Subscribe to Booth Recruiters

  useEffect(() => {
    if (!jobFair?.id || !booth?.id) return

    const disposables: any[] = []
    disposablesRef.current = disposables

    // // Setting up Queue User Subscriptions
    const queueSubscriptionDisposable = setupQueueSubscriptionsByBoothId(booth.id)

    const subscription = RxSubscription.onChangeQueueUserByBoothIdObservable().subscribe(
      async (event) => {
        if (!event?.value) return
        if (!isMounted()) return
        await refresh(false, true, false)
      }
    )

    const subscriptionBoothViewCounter =
      ActiveBoothViewCounterState.getBoothViewCounterObservable().subscribe(async (counter) => {
        if (!isMounted()) return
        await refresh(false, true, true)
      })

    let disposable = LocalAppEventPubSub.subscribe(async (eventType, event) => {
      if (!isMounted()) return
      if (event.type === LocalAppEventType.AmplifySubscriptionError) {
        setTimeout(() => {
          if (isMounted()) window.location.reload()
        }, 2000)
      }
      if (event.type === LocalAppEventType.AvailabilityUpdateEvent) {
        setTimeout(async () => {
          if (isMounted()) await _debouncedRefresh.run()
        }, 2000)
      }
    })

    disposables.push(disposable)

    return () => {
      subscription.unsubscribe()
      disposables.forEach((disposable) => {
        if (disposable) disposable.dispose()
      })
      subscriptionBoothViewCounter.unsubscribe()
      if (queueSubscriptionDisposable) queueSubscriptionDisposable.dispose()
      disposablesRef.current = []
    }
  }, [booth?.id, jobFair?.id])

  const _refresh = async (refreshQueueUsers: boolean = true, refreshRecruiters: boolean = true) => {
    if (!isMounted()) return
    if (!jobFair?.id || !booth?.id || !user?.id) return
    console.log(`refreshing queue at ${new Date()}`)
    try {
      refreshing.current = true

      const vjfpService = new VJFPUserService()
      const queueUserService = new QueueUserService()

      const getQueueUsers = async () => {
        const queueUsers: QueueUser[] | undefined = await queueUserService.getQueueUsersByBoothId(
          jobFair.id,
          booth.id
        )
        return queueUsers || []
      }

      const getVJFPUsers = async (users: QueueUser[]) => {
        const vjfpUsers = await vjfpService.getByIds(
          EmptyGuid,
          users.map((x) => x.vjfpUserId)
        )
        return vjfpUsers || []
      }

      const getBoothRecruiters = async () => {
        const recruiterIds: number[] = []
        if (booth.recruiters?.length) {
          booth.recruiters.forEach((r) => {
            if (r) {
              recruiterIds.push(r)
            }
          })
          const recruiters = await vjfpService.getByIds(user.organizationId, recruiterIds)
          if (recruiters?.length)
            return recruiters.sort(function (a, b) {
              const p1 = a.presenceStatus === UserStatus.LOGGED_IN ? 1 : 0
              const p2 = b.presenceStatus === UserStatus.LOGGED_IN ? 1 : 0
              return p2 - p1
            })
        }
        return []
      }

      // By default refreshQueueUsers is true
      if (refreshQueueUsers) {
        // Fetch Booth's Queue Users from DB
        try {
          const _queueUsers = ((await getQueueUsers()) || []).filter((v) => v.vjfpUserId != null)

          const vjfpJobSeekers = await getVJFPUsers(_queueUsers)

          const vjfpJobSeekersFiltered = vjfpJobSeekers.filter((v) => v) as VJFPUser[]
          // Removed manual presence subscription management

          if (!isMounted()) return
          setVJFPUsers(vjfpJobSeekersFiltered || [])

          const _qu = _queueUsers.find((x) => x.priority === -1 && x.interviewer === user.id)
          if (_qu && !quInterviewTimeoutRef.current) {
            quInterviewTimeoutRef.current = setTimeout(async () => {
              try {
                if (!isMounted()) return
                quInterviewTimeoutRef.current = undefined

                const interviewSessionService = new InterviewSessionService()
                const i = await interviewSessionService.getById(jobFair.id, _qu.id)

                if (i.status === VideoSessionStatus.IN_PROGRESS) {
                  const url = UrlParamReplace(RecruiterRoutes.Interview, ':sessionId', i.id)
                  history.push(url)
                } else if (i.status === VideoSessionStatus.ENDED) {
                  RxSubscription.clearInterviewSessionByRecruiterId()
                  if (isMounted()) setShowEnded(true)

                  if (i.endReason === EndReason.TIMED_OUT) {
                    if (isMounted())
                      setAlertMessage(
                        'Interview ended -- This session timed out due to the job seeker not joining the session.'
                      )
                  }
                }
              } catch (error) {
                console.error('Error handling interview session:', error)
              }
            }, 60000)
          }

          if (!isMounted()) return
          setQueueUsers(_queueUsers)
        } catch (error) {
          console.error(error)
        } finally {
          refreshing.current = false
        }
      }

      // By default refreshQueueUsers is true
      if (refreshRecruiters) {
        try {
          // Fetch Booth's Recruiters
          const boothRecruiters = await getBoothRecruiters()
          const boothRecruitersFiltered = boothRecruiters.filter((v) => v) as VJFPUser[]
          const uniqueRecruiters = _.uniqBy(boothRecruitersFiltered, 'id') // Ensure unique recruiters by ID

          // Removed manual presence subscription management

          if (!isMounted()) return

          uniqueRecruiters.forEach((j) => {
            if (j.id == user.id) {
              setDisableStartInterview(!j.isAvailableForChat)
            }
          })

          if (!isMounted()) return
          setRecruiters(uniqueRecruiters || []) // Update state with unique recruiters
        } catch (error) {
          console.error(error)
        } finally {
          refreshing.current = false
        }
      }
    } catch (err) {
      console.error(err)
    } finally {
      refreshing.current = false
    }
  }

  useEffect(() => {
    vjfpUsersWithPresence.forEach((u) => {
      if (u?.avatar) getS3ObjectUrl(u.avatar)
    })
  }, [vjfpUsersWithPresence])

  useEffect(() => {
    recruitersWithPresence.forEach((u) => {
      if (u?.avatar) getS3ObjectUrl(u.avatar)
    })
  }, [recruitersWithPresence])

  const _debouncedRefresh = useDebounceFn(_refresh, 2000)

  const refresh = async (
    showLoader: boolean = false,
    refreshQueueUsers: boolean = true,
    refreshRecruiters: boolean = true
  ) => {
    if (!isMounted()) return
    if (refreshing.current) {
      return
    }

    if (showLoader) {
      setLoading(true)
    }
    try {
      await _debouncedRefresh.run(refreshQueueUsers, refreshRecruiters)
    } finally {
      if (isMounted()) setLoading(false)
    }
  }

  const navigateToManageBooth = () => {
    if (booth?.id)
      history.push(UrlParamReplace(RecruiterRoutes.ManageBoothInfo, ':boothId', booth.id))
  }

  const navigateToBoothPreview = () => {
    if (booth?.id)
      history.push({
        pathname: UrlParamReplace(RecruiterRoutes.BoothPreview, ':boothId', booth.id),
        state: {booth},
      })
  }

  const processQueue = async () => {
    if (!jobFair?.id) return

    try {
      setRefreshDisable(true)
      refresh(true)
      setTimeout(() => setRefreshDisable(false), 5000)
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    if (!jobFair || !user || !booth?.id) return

    const disposable = setupInterviewSessionSubscriptionsByRecruiterId(user.id)

    const subscription = RxSubscription.onChangeInterviewSessionByRecruiterIdObservable().subscribe(
      (event) => {
        if (!event?.value) return

        const interviewSession = event.value

        if (interviewSession.status === VideoSessionStatus.IN_PROGRESS) {
          const url = UrlParamReplace(RecruiterRoutes.Interview, ':sessionId', interviewSession.id)
          history.push(url)
        } else if (interviewSession.status === VideoSessionStatus.ENDED) {
          RxSubscription.clearInterviewSessionByRecruiterId()
          if (isMounted()) setShowEnded(true)
          if (interviewSession.endReason === EndReason.TIMED_OUT) {
            if (isMounted())
              setAlertMessage(
                'Interview ended -- This session timed out due to the job seeker not joining the session.'
              )
          }
        }
      }
    )

    return () => {
      disposable?.dispose()
      subscription?.unsubscribe()
      console.log('Disposing onChangeInterviewSessionByRecruiterIdObservable')
    }
  }, [booth?.id, jobFair, user])

  const sendBroadcastMessage = async () => {
    if (!jobFair?.id) return

    setBroadCastModal(false)
    if (broadCastMessage && booth && user) {
      const messageBroadcasted = await messageService.broadcastMessage(
        jobFair.id,
        broadCastType === 'Queue' ? 'queue_' + booth.id : booth.id,
        broadCastMessage,
        user.id
      )
      if (broadCastType === 'Booth') {
        // boothService.getBoothsVisitors(booth.id).then(bv => {
        //     bv?.forEach((v: BoothVisitor | null) => {
        //         if (messageBroadcasted?.id && v?.vjfpUserId)
        //             messageService.createMessageRecipient(messageBroadcasted.id, v.vjfpUserId, jobFair.id);
        //     })
        // });
      } else {
        queueUserService.getQueueUsersByBoothId(jobFair.id, booth.id).then((qu) => {
          qu?.forEach((u: any) => {
            if (messageBroadcasted?.id && u?.vjfpUserId)
              messageService.createMessageRecipient(messageBroadcasted.id, u.vjfpUserId, jobFair.id)
          })
        })
      }
    }
  }

  const closeBroadCastModal = () => {
    setBroadCastModal(false)
  }

  const getvjfpUserStatus = (
    vjfpUser: VJFPUser | undefined,
    queueUserStatus?: QueueUserStatus | null
  ) => {
    if (vjfpUser?.isAvailableForChat === false) return UserStatus.AWAY
    const userStatus = vjfpUser?.presenceStatus || UserStatus.LOGGED_OUT
    if (userStatus === UserStatus.LOGGED_IN) {
      return queueUserStatus || userStatus
    }
    return userStatus
  }

  const handleUpdateBooth = async (values: CreateBoothInput) => {
    if (!user?.id || !jobFair?.id) return
    values.organizationId = user.organizationId
    const boothService = new BoothService()
    if (booth?.id) {
      await boothService.updateBooth(booth.jobFairId, booth.id, values)
    }
    await fetchBoothByRecruiter()
    setShowManageBooth(false)
  }

  const processedQueueUsers = queueUsers
    .map((candidate) => {
      const vjfpUser = vjfpUsersWithPresence.find((v) => v.id === candidate.vjfpUserId)
      const status = getvjfpUserStatus(vjfpUser, candidate.status)
      return {candidate, vjfpUser, status}
    })
    .filter(
      (item) => item.status !== UserStatus.LOGGED_OUT && item.status !== UserStatus.DISCONNECTED
    )

  return (
    <div className='chatPage recruiterDashboard'>
      {!loading && !booth?.id && !user?.roles?.includes(Roles.EmployerAdmin) && (
        <div className='d-flex justify-content-center align-items-center' style={{height: '100vh'}}>
          <Alert variant='danger'>
            <Alert.Heading>
              Your Employer-Administrator has not yet assigned you to a booth, but will be doing so
              shortly{' '}
            </Alert.Heading>
          </Alert>
        </div>
      )}
      {booth?.id && (
        <>
          <AppTopNav title={booth?.name} breadcrumbs={props.breadcrumbs} toolbar={<AvailabilitySelector />}>
            <OverlayTrigger overlay={<Tooltip id='boothInfo'>Manage Booth Info</Tooltip>}>
              <Button
                className='m-2'
                onClick={() => {
                  setShowManageBooth(true)
                }}
              >
                <PencilSquare />
                <small className='ms-2'>Booth Info</small>
              </Button>
            </OverlayTrigger>
            <OverlayTrigger overlay={<Tooltip id='jobseekerBoothView'>Booth Preview</Tooltip>}>
              <Button className='m-2' onClick={navigateToBoothPreview}>
                <BoxArrowInUpRight />
                <small className='ms-2'>Booth Preview</small>
              </Button>
            </OverlayTrigger>
            <OverlayTrigger overlay={<Tooltip id='refresh'>Refresh Queue</Tooltip>}>
              <Button
                variant='danger'
                className='m-2'
                disabled={refreshDisable}
                onClick={processQueue}
              >
                <Recycle />
                <small className='ms-2'>Refresh Queue</small>
              </Button>
            </OverlayTrigger>
          </AppTopNav>
          <Container>
            <Row>
              <Col md={6}>
                <Row>
                  <Col className={'listTitle'}>
                    <h5>
                      {/*{booth?.name}*/} <span style={{fontWeight: 'normal'}}>Booth Queue:</span>{' '}
                      {processedQueueUsers?.length || 0} in queue
                    </h5>
                    {/* <span title="Refresh Queue" onClick={() => refresh(true)} style={{cursor: "pointer"}} className="float-end m-2"><ArrowClockwise size={20}/></span> */}
                  </Col>
                </Row>
                <BlockUi
                  id='boothLoadingContainer'
                  tag='div'
                  className={'jobSeekerListing'}
                  blocking={loading}
                >
                  {processedQueueUsers.map(({candidate, vjfpUser, status}, index: number) => {
                    const interviewer = recruitersWithPresence.find(
                      (v) => v.id === candidate.interviewer
                    )
                    return (
                      <JobSeekerListItem
                        key={index}
                        index={index}
                        initials={getInitials(vjfpUser?.firstName, vjfpUser?.lastName)}
                        profilePicUrl={
                          vjfpUser?.avatar ? s3ObjectCache[vjfpUser.avatar] : undefined
                        }
                        name={(vjfpUser?.firstName || '') + ' ' + (vjfpUser?.lastName || '')}
                        score={candidate.score || 0}
                        maxScore={totalQuestions || 0}
                        isInvited={candidate.isInvited || false}
                        address={vjfpUser?.address || ''}
                        summary={vjfpUser?.summary || ''}
                        interviewer={
                          interviewer?.firstName && interviewer?.lastName
                            ? (interviewer.firstName || '') + ' ' + (interviewer.lastName || '')
                            : ''
                        }
                        status={status}
                        onInterviewClick={
                          user?.id === candidate.interviewer
                            ? () => {
                                const url = UrlParamReplace(
                                  RecruiterRoutes.Interview,
                                  ':sessionId',
                                  candidate.id
                                )
                                history.push(url)
                              }
                            : undefined
                        }
                      />
                    )
                  })}
                  {processedQueueUsers.length === 0 && <p> No candidates waiting to interview </p>}
                </BlockUi>
              </Col>
              <Col md={6}>
                <div className='listTitle'>
                  <h5>Team Members</h5>
                </div>
                <BlockUi tag='div' className={'jobSeekerListing recruiterList'} blocking={loading}>
                  {recruitersSorted.map(
                    (vjfpUser, index) =>
                      vjfpUser && (
                        <RecruiterListItem
                          index={index}
                          key={index}
                          initials={getInitials(vjfpUser.firstName, vjfpUser.lastName)}
                          profilePicUrl={
                            vjfpUser.avatar ? s3ObjectCache[vjfpUser.avatar] : undefined
                          }
                          name={(vjfpUser.firstName || '') + ' ' + (vjfpUser.lastName || '')}
                          designation={vjfpUser.job_title || 'Recruiter'}
                          address={vjfpUser.address || ''}
                          status={getUserStatus(vjfpUser)}
                          greetings={vjfpUser.greeting || ''}
                        />
                      )
                  )}
                </BlockUi>

                <AppModal
                  show={showBroadCastModal}
                  onSuccess={sendBroadcastMessage}
                  onHide={closeBroadCastModal}
                  title='Broadcast message'
                  variant='PlainText'
                  successText='Broadcast'
                  rejectText='No Thanks'
                >
                  <Form>
                    <Form.Group className='mb-3' controlId='broadcastTo'>
                      <Form.Label className='fs-4 fw-bold'>Broadcast To</Form.Label>
                      <div className='mt-1'>
                        <Form.Check
                          id='broad_queue'
                          inline
                          onChange={() => {
                            setBroadCastType('Queue')
                          }}
                          label='Queue'
                          checked={broadCastType === 'Queue'}
                          name='broadcastGroup'
                          type='radio'
                        />
                        <Form.Check
                          id='broad_queue'
                          inline
                          onChange={() => {
                            setBroadCastType('Booth')
                          }}
                          label='Booth'
                          checked={broadCastType === 'Booth'}
                          name='broadcastGroup'
                          type='radio'
                        />
                      </div>
                    </Form.Group>
                    <Form.Group className='mb-3' controlId='broadcastMsg'>
                      <Form.Label className='fs-4 fw-bold'>Broadcast Message</Form.Label>
                      <Form.Control
                        as='textarea'
                        rows={3}
                        placeholder=''
                        onChange={(e) => {
                          setBroadCastMessage(e.target.value)
                        }}
                      />
                    </Form.Group>
                  </Form>
                </AppModal>

                <AlertModal
                  show={!!errorMessage}
                  title={errorMessage}
                  onOK={() => setErrorMessage('')}
                ></AlertModal>
              </Col>
            </Row>
          </Container>

          <AlertModal
            show={showEnded}
            title='Interview ended'
            okText='OK'
            onOK={() => setShowEnded(false)}
          >
            {alertMessage}
          </AlertModal>

          <Modal size='lg' show={showManageBooth} onHide={() => setShowManageBooth(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Edit Booth</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <CreateEditBooth
                orgName=''
                onSubmit={handleUpdateBooth}
                booth={booth}
                companyImageUrl={companyImageUrl}
              />
            </Modal.Body>
          </Modal>
        </>
      )}
    </div>
  )
}
